import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { menuConfig } from '../../config/menuConfig';
import { renderIcon } from '../../utils/iconMap';

const Badge = ({ type, text }) => {
  const badgeClasses = {
    success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-lg ${badgeClasses[type] || badgeClasses.info}`}>
      {text}
    </span>
  );
};

const MenuItem = ({ item, level = 0, isCollapsed }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const isActive = location.pathname === item.path;
  const hasChildren = item.children && item.children.length > 0;
  
  // Check if any child is active (for parent highlighting)
  const hasActiveChild = hasChildren && item.children.some(child => 
    location.pathname === child.path || location.pathname.startsWith(child.path + '/')
  );
  const isParentActive = isActive || hasActiveChild;

  const handleToggle = () => {
    if (hasChildren && !isCollapsed) {
      setIsOpen(!isOpen);
    }
  };

  const itemClasses = `
    flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg
    transition-all duration-200 group relative overflow-hidden
    ${isActive 
      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25' 
      : hasActiveChild
        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50 hover:shadow-sm'
    }
    ${level > 0 ? 'ml-3 mr-1' : ''}
    ${isCollapsed ? 'justify-center' : 'justify-between'}
  `;

  if (item.type === 'item' && item.path) {
    return (
      <div className="mb-1" title={isCollapsed ? item.label : ''}>
        <Link to={item.path} className={itemClasses}>
          <div className={`flex items-center ${isCollapsed ? '' : 'gap-3'}`}>
            {renderIcon(item.icon, { 
              size: 18,
              className: isActive 
                ? 'text-white' 
                : hasActiveChild
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'
            })}
            <span className={`font-medium transition-all duration-300 ${
              isCollapsed 
                ? 'opacity-0 translate-x-2 w-0 overflow-hidden' 
                : 'opacity-100 translate-x-0 w-auto'
            }`}>
              {item.label}
            </span>
            {!isCollapsed && item.badge && <Badge type={item.badge.type} text={item.badge.text} />}
          </div>
        </Link>
      </div>
    );
  }

  if (item.type === 'group' && hasChildren) {
    return (
      <div className="mb-1" title={isCollapsed ? item.label : ''}>
        <button onClick={handleToggle} className={itemClasses}>
          <div className={`flex items-center ${isCollapsed ? '' : 'gap-3'}`}>
            {renderIcon(item.icon, { 
              size: 18,
              className: hasActiveChild
                ? 'text-blue-600 dark:text-blue-400'
                : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'
            })}
            <span className={`font-medium transition-all duration-300 ${
              isCollapsed 
                ? 'opacity-0 translate-x-2 w-0 overflow-hidden' 
                : 'opacity-100 translate-x-0 w-auto'
            }`}>
              {item.label}
            </span>
            {!isCollapsed && item.badge && <Badge type={item.badge.type} text={item.badge.text} />}
          </div>
          <div className={`text-gray-400 dark:text-gray-500 transition-all duration-300 ${
            isCollapsed 
              ? 'opacity-0 translate-x-2 w-0 overflow-hidden' 
              : 'opacity-100 translate-x-0 w-auto'
          }`}>
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </div>
        </button>
        {isOpen && !isCollapsed && (
          <div className="mt-2 space-y-1 mb-2 overflow-hidden">
            <div className="animate-in slide-in-from-left-2 duration-200">
              {item.children.map((child, index) => (
                <div
                  key={child.id}
                  className="animate-in fade-in duration-200"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <MenuItem item={child} level={level + 1} isCollapsed={isCollapsed} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

const Sidebar = ({ isOpen, onToggle }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isCollapsed = !isHovered;

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`
          fixed inset-y-0 left-0 z-30 transform transition-all duration-300 ease-in-out
          bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700 shadow-xl
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:static lg:inset-0
          ${isCollapsed ? 'w-16' : 'w-72'}
        `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center py-6 border-b border-gray-200 dark:border-gray-700 px-3 min-h-[88px]">
            <div className="flex items-center w-full">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-lg font-bold text-white">A</span>
              </div>
              <div className={`ml-3 transition-all duration-300 overflow-hidden ${
                isCollapsed 
                  ? 'opacity-0 translate-x-2 w-0' 
                  : 'opacity-100 translate-x-0 w-auto'
              }`}>
                <h1 className="text-lg font-bold text-gray-800 dark:text-white whitespace-nowrap">
                  Admin Panel
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Management System</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 py-6 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 ${isCollapsed ? 'px-2' : 'px-4'}`}>
            {menuConfig.map(item => (
              <MenuItem key={item.id} item={item} isCollapsed={isCollapsed} />
            ))}
          </nav>

          {/* Footer */}
          <div className={`border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 p-3 min-h-[80px]`}>
            <div className="flex items-center w-full">
              <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0" title={isCollapsed ? "Admin User" : ""}>
                <span className="text-sm font-semibold text-white">A</span>
              </div>
              <div className={`ml-3 flex-1 min-w-0 transition-all duration-300 overflow-hidden ${
                isCollapsed 
                  ? 'opacity-0 translate-x-2 w-0' 
                  : 'opacity-100 translate-x-0 w-auto'
              }`}>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate whitespace-nowrap">Admin User</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate whitespace-nowrap">admin@example.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
